// Trackside spectators (RALLY_GOAL.md §5 P1-6) as cheap impostors: every person at every hotspot is
// one camera-facing quad (turning about the vertical only) in a single instanced draw, painted from
// a small canvas atlas of outfits and poses. Idle crowds shift their weight; as the car closes in
// they clap, wave and hop — all in the vertex shader, so they cost no CPU per frame. A soft contact
// shadow under each keeps them on the ground.
import * as THREE from 'three/webgpu';
import {
  cameraPosition,
  cameraViewMatrix,
  clamp,
  float,
  fract,
  fwidth,
  instancedBufferAttribute,
  length,
  log2,
  max,
  normalize,
  positionGeometry,
  sin,
  smoothstep,
  step,
  texture,
  uniform,
  varying,
  vec2,
  vec3,
  vec4,
  cross,
} from 'three/tsl';
import type { Node } from 'three/webgpu';
import { CROWD_VARIANTS, PERSON_STRIDE } from '../world/crowd';
import type { PropInstance } from '../world/types';
import { setEnvScale } from './matte';

const POSES = 3;
const CW = 96;
const CH = 192;

interface Outfit {
  jacket: string;
  trousers: string;
  skin: string;
  hair: string;
  hat?: { kind: 'cap' | 'beanie' | 'hood'; color: string };
  stripe?: string;
}

const OUTFITS: Outfit[] = [
  { jacket: '#a8231c', trousers: '#1d2029', skin: '#e1b38e', hair: '#3a2a1c', hat: { kind: 'cap', color: '#1b1d22' } },
  { jacket: '#e0661c', trousers: '#2c3a4e', skin: '#c68d63', hair: '#1a1410', stripe: '#d8d8d0' },
  { jacket: '#1f3857', trousers: '#3a3326', skin: '#f0c7a4', hair: '#8a6a3a', hat: { kind: 'beanie', color: '#b02a2a' } },
  { jacket: '#2f4a2c', trousers: '#1c1f26', skin: '#8d5a3b', hair: '#120e0c' },
  { jacket: '#1d1e21', trousers: '#47494e', skin: '#e3b897', hair: '#5a3e24', hat: { kind: 'hood', color: '#1d1e21' } },
  { jacket: '#cdb21e', trousers: '#262a33', skin: '#d7a37a', hair: '#2a2018', stripe: '#9a9a90' },
  { jacket: '#2c66a8', trousers: '#2e3440', skin: '#5c3a26', hair: '#0e0b09', hat: { kind: 'cap', color: '#d6d2c8' } },
  { jacket: '#6a6e74', trousers: '#2a3b55', skin: '#f2cdb0', hair: '#b08a52', hat: { kind: 'beanie', color: '#24324a' } },
];

/** Paint one person (front view) into the cell at (ox, oy). Pose 0 idle, 1 clapping, 2 arms up. */
function paintPerson(g: CanvasRenderingContext2D, ox: number, oy: number, o: Outfit, pose: number, k: number): void {
  const cx = ox + CW / 2;
  const top = oy + 8;
  const shade = (col: string, f: number) => {
    const n = parseInt(col.slice(1), 16);
    const c = (s: number) => Math.round(Math.min(255, ((n >> s) & 255) * f));
    return `rgb(${c(16)},${c(8)},${c(0)})`;
  };
  const rr = (x: number, y: number, w: number, h: number, r: number, col: string) => {
    g.fillStyle = col;
    g.beginPath();
    g.roundRect(x, y, w, h, r);
    g.fill();
  };
  const limb = (x0: number, y0: number, x1: number, y1: number, w: number, col: string) => {
    g.strokeStyle = col;
    g.lineWidth = w;
    g.lineCap = 'round';
    g.beginPath();
    g.moveTo(x0, y0);
    g.lineTo(x1, y1);
    g.stroke();
  };
  const hand = (x: number, y: number) => {
    g.fillStyle = o.skin;
    g.beginPath();
    g.arc(x, y, 5, 0, Math.PI * 2);
    g.fill();
  };
  const wide = k % 2 === 0 ? 2 : 0;
  // Legs and boots.
  rr(cx - 17 - wide / 2, top + 92, 16, 84, 5, o.trousers);
  rr(cx + 1 + wide / 2, top + 92, 16, 84, 5, shade(o.trousers, 0.82));
  rr(cx - 19 - wide / 2, top + 170, 19, 9, 3, '#17140f');
  rr(cx + wide / 2, top + 170, 19, 9, 3, '#17140f');
  // Arms behind the torso for the idle pose.
  const sl = cx - 21 - wide;
  const sr = cx + 21 + wide;
  const sy = top + 34;
  if (pose === 0) {
    limb(sl, sy, sl - 4, sy + 52, 11, shade(o.jacket, 0.85));
    limb(sr, sy, sr + 4, sy + 52, 11, shade(o.jacket, 0.75));
    hand(sl - 4, sy + 58);
    hand(sr + 4, sy + 58);
  }
  // Torso (jacket), with a darker side for volume and an optional reflective stripe.
  rr(cx - 22 - wide, top + 28, 44 + wide * 2, 72, 10, o.jacket);
  rr(cx + 4, top + 30, 18 + wide, 68, 8, shade(o.jacket, 0.84));
  if (o.stripe) rr(cx - 22 - wide, top + 70, 44 + wide * 2, 5, 1, o.stripe);
  rr(cx - 2, top + 32, 3, 62, 1, shade(o.jacket, 0.6));
  // Arms in front for clapping / raised.
  if (pose === 1) {
    limb(sl, sy, sl - 2, sy + 30, 11, shade(o.jacket, 0.9));
    limb(sr, sy, sr + 2, sy + 30, 11, shade(o.jacket, 0.8));
    limb(sl - 2, sy + 30, cx - 5, sy + 14, 10, o.jacket);
    limb(sr + 2, sy + 30, cx + 5, sy + 14, 10, shade(o.jacket, 0.9));
    hand(cx - 4, sy + 11);
    hand(cx + 4, sy + 11);
  } else if (pose === 2) {
    limb(sl, sy, sl - 12, sy - 24, 11, o.jacket);
    limb(sr, sy, sr + 12, sy - 24, 11, shade(o.jacket, 0.88));
    limb(sl - 12, sy - 24, sl - 14, sy - 46, 10, o.jacket);
    limb(sr + 12, sy - 24, sr + 14, sy - 46, 10, shade(o.jacket, 0.88));
    hand(sl - 14, sy - 50);
    hand(sr + 14, sy - 50);
  }
  // Neck, head, hair, hat.
  rr(cx - 5, top + 20, 10, 12, 3, shade(o.skin, 0.85));
  g.fillStyle = o.skin;
  g.beginPath();
  g.ellipse(cx, top + 12, 10, 12, 0, 0, Math.PI * 2);
  g.fill();
  // Face: brows, eyes and a mouth (blank ovals read as masks at a distance).
  g.fillStyle = shade(o.skin, 0.88);
  g.beginPath();
  g.ellipse(cx, top + 17, 8, 4.5, 0, 0, Math.PI);
  g.fill();
  g.fillStyle = '#1a1410';
  g.fillRect(cx - 6, top + 10, 4, 3);
  g.fillRect(cx + 2, top + 10, 4, 3);
  g.fillStyle = shade(o.hair, 1);
  g.fillRect(cx - 7, top + 7.5, 5, 1.5);
  g.fillRect(cx + 2, top + 7.5, 5, 1.5);
  g.fillStyle = '#5a2a22';
  g.fillRect(cx - 3, top + 17, 6, 2);
  g.fillStyle = o.hair;
  g.beginPath();
  g.ellipse(cx, top + 6, 10.5, 7, 0, Math.PI, Math.PI * 2);
  g.fill();
  if (o.hat?.kind === 'cap') {
    rr(cx - 11, top - 1, 22, 9, 4, o.hat.color);
    rr(cx - 12, top + 6, 24, 3, 1, shade(o.hat.color, 0.7));
  } else if (o.hat?.kind === 'beanie') {
    rr(cx - 11, top - 3, 22, 12, 6, o.hat.color);
  } else if (o.hat?.kind === 'hood') {
    g.fillStyle = o.hat.color;
    g.beginPath();
    g.ellipse(cx, top + 10, 14, 15, 0, Math.PI * 1.02, Math.PI * 1.98);
    g.fill();
    rr(cx - 14, top + 6, 5, 20, 2, o.hat.color);
    rr(cx + 9, top + 6, 5, 20, 2, o.hat.color);
  }
}

let atlas: THREE.CanvasTexture | null = null;
function crowdAtlas(): THREE.CanvasTexture {
  if (atlas) return atlas;
  const c = document.createElement('canvas');
  c.width = CW * CROWD_VARIANTS;
  c.height = CH * POSES;
  const g = c.getContext('2d')!;
  for (let p = 0; p < POSES; p++) for (let v = 0; v < CROWD_VARIANTS; v++) paintPerson(g, v * CW, p * CH, OUTFITS[v % OUTFITS.length], p, v);
  atlas = new THREE.CanvasTexture(c);
  atlas.colorSpace = THREE.SRGBColorSpace;
  atlas.anisotropy = 4;
  return atlas;
}

export class Crowds {
  readonly group = new THREE.Group();
  /** Hotspot centres (for the crowd audio). */
  readonly sites: { x: number; z: number }[] = [];
  private readonly car = uniform(new THREE.Vector3(1e6, 0, 1e6));
  private readonly time = uniform(0);

  constructor(props: readonly PropInstance[], heightAt: (x: number, z: number) => number) {
    this.group.name = 'spectators';
    const people: number[] = [];
    for (const p of props) {
      if (p.type !== 'spectators' || !p.people || p.people.length === 0) continue;
      this.sites.push({ x: p.x, z: p.z });
      people.push(...p.people);
    }
    const n = people.length / PERSON_STRIDE;
    if (n === 0) return;
    const centre = new Float32Array(n * 3);
    const info = new Float32Array(n * 4);
    const normal = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      const o = i * PERSON_STRIDE;
      const x = people[o];
      const z = people[o + 2];
      const y = heightAt(x, z);
      centre.set([x, y - 0.05, z], i * 3);
      info.set([people[o + 3], people[o + 4], people[o + 5], 0.93 + 0.14 * ((i * 0.618) % 1)], i * 4);
      const nx = heightAt(x - 0.4, z) - heightAt(x + 0.4, z);
      const nz = heightAt(x, z - 0.4) - heightAt(x, z + 0.4);
      const l = Math.hypot(nx, 0.8, nz);
      normal.set([nx / l, 0.8 / l, nz / l], i * 3);
    }
    const cAttr = new THREE.InstancedBufferAttribute(centre, 3);
    const iAttr = new THREE.InstancedBufferAttribute(info, 4);
    const nAttr = new THREE.InstancedBufferAttribute(normal, 3);
    const c = instancedBufferAttribute(cAttr) as unknown as Node<'vec3'>;
    const inf = instancedBufferAttribute(iAttr) as unknown as Node<'vec4'>;
    const variant = inf.x;
    const height = inf.y;
    const phase = inf.z;
    const shadeK = inf.w;

    // --- People.
    const q = positionGeometry.xy;
    const toCam = cameraPosition.sub(c);
    const dir = normalize(vec3(toCam.x, 0, toCam.z).add(vec3(1e-4, 0, 0)));
    const right = vec3(dir.z, 0, dir.x.negate());
    const dx = this.car.x.sub(c.x);
    const dz = this.car.z.sub(c.z);
    const excite = smoothstep(float(95), float(28), dx.mul(dx).add(dz.mul(dz)).sqrt());
    const t = this.time;
    const hop = excite.mul(max(sin(t.mul(9).add(phase.mul(40))), 0)).mul(0.17).mul(step(0.45, phase));
    const sway = sin(t.mul(1.1).add(phase.mul(30))).mul(0.035).add(excite.mul(sin(t.mul(5).add(phase.mul(17))).mul(0.05)));
    const h = height.mul(CH / (CH - 12));
    const w = h.mul(CW / CH);
    const pos = c.add(right.mul(q.x.mul(w).add(sway.mul(q.y)))).add(vec3(0, q.y.mul(h).add(hop), 0));
    const pose = step(0.35, excite).mul(float(1).add(step(0.5, fract(t.mul(1.7).add(phase)))));
    const uvp = varying(vec2(variant.add(q.x).add(0.5).div(CROWD_VARIANTS), float(POSES - 1).sub(pose).add(q.y).div(POSES)));
    const tex = crowdAtlas();
    const m = new THREE.MeshStandardNodeMaterial({ alphaTest: 0.5, side: THREE.DoubleSide, roughness: 0.92, metalness: 0 });
    m.positionNode = pos;
    const s = texture(tex, uvp);
    m.colorNode = s.rgb.mul(varying(shadeK));
    // Mip levels average the cut-out alpha away; boost it with the level in use (as the tree billboards do).
    const lod = log2(max(fwidth(uvp.x).mul(CW * CROWD_VARIANTS), fwidth(uvp.y).mul(CH * POSES)).max(1e-4));
    m.opacityNode = s.a.mul(float(1).add(clamp(lod, 0, 6).mul(0.4)));
    // Rounded "volume" normal: toward the camera, up, and out at the sides.
    const nWorld = varying(normalize(dir.mul(0.75).add(vec3(0, 0.5, 0)).add(right.mul(q.x.mul(0.9)))));
    m.normalNode = normalize(cameraViewMatrix.mul(vec4(nWorld, 0)).xyz);
    setEnvScale(m, 0.6);
    const quad = new THREE.PlaneGeometry(1, 1).translate(0, 0.5, 0);
    const mesh = new THREE.Mesh(quad, m);
    mesh.count = n;
    mesh.frustumCulled = false;
    mesh.receiveShadow = true;
    mesh.castShadow = false;
    mesh.name = 'spectators-people';
    this.group.add(mesh);

    // --- Contact shadows, tilted to the ground under each person.
    const nr = instancedBufferAttribute(nAttr) as unknown as Node<'vec3'>;
    const t1 = normalize(cross(nr, vec3(0, 0, 1)));
    const t2 = cross(t1, nr);
    const b = positionGeometry.xy;
    const size = float(0.95);
    const bm = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false });
    bm.positionNode = c.add(vec3(0, 0.05, 0)).add(nr.mul(0.04)).add(t1.mul(b.x.mul(size))).add(t2.mul(b.y.mul(size)));
    bm.colorNode = vec3(0.02, 0.018, 0.012);
    bm.opacityNode = float(1).sub(smoothstep(float(0.08), float(0.5), length(b))).mul(0.5);
    bm.polygonOffset = true;
    bm.polygonOffsetFactor = -2;
    bm.polygonOffsetUnits = -2;
    const blobs = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), bm);
    blobs.count = n;
    blobs.frustumCulled = false;
    blobs.renderOrder = 1;
    blobs.name = 'spectators-shadows';
    this.group.add(blobs);
  }

  /** Per frame: wall-clock seconds and the car's position (the crowd reacts as it approaches). */
  update(seconds: number, car: THREE.Vector3): void {
    this.time.value = seconds;
    this.car.value.copy(car);
    // Out of sight beyond ~700 m (and through the forest well before): skip the two draws.
    let near = false;
    for (const s of this.sites) if ((s.x - car.x) ** 2 + (s.z - car.z) ** 2 < 700 * 700) near = true;
    this.group.visible = near;
  }
}
